import {Stack, Typography} from '@mui/material';
import React from 'react';

import {DataInfo} from 'presentation/components';
import {translations} from 'presentation/translations-new';
import {formatText} from 'presentation/utils';
import {HealthSpecialtiesSection} from '../HealthSpecialtiesSection';
import {StyledContainer, StyledStack} from './styles';
import {HealthServicesSectionProps} from './types';
import {FeaturesEnum} from 'presentation/constants/featuresFlagEnum';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {HealthGuideServicesTypeEnum} from 'presentation/constants';
import {useHealthProviderDetailsQuery} from 'presentation/modules/guide/health/queries/useHealthProviderDetailsQuery';
import {getServiceInfo} from 'presentation/modules/guide/shared/utils/getServiceInfo';

const {guideDetails} = translations['pt-br'].privateFlow.guideFlow;

export function HealthServicesSection({provider}: HealthServicesSectionProps) {
	const {isEnabled: isSemanticSearchEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SEMANTIC_SEARCH_MEDICAL_GUIDE,
	);
	const {isEnabled: isHideSpecialtiesProceduresFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_HIDE_SPECIALTIES_PROCEDURES_HEALTH_GUIDE,
	);
	const appointmentTypeFormatted = provider.tipoConsulta?.trim();
	const {codigo, cdUf, nmCidade, tipo} = provider;

	const {
		isLoading: loading,
		data: providerDetail,
		isError,
	} = useHealthProviderDetailsQuery({
		cdPrestador: String(codigo),
		cdRedeHap: 'LN',
		nmCidade,
		dsTipo: tipo,
		cdUf,
	});

	const serviceInfo = getServiceInfo(provider.tipo, isSemanticSearchEnabled);

	const healthServicesData = {
		careTypes: appointmentTypeFormatted ? formatText(appointmentTypeFormatted) : undefined,
		servicesType: serviceInfo.label,
		appointment: provider.modalidade?.consulta,
		hospitalization: provider.modalidade?.internacao,
		maternity: provider.modalidade?.maternidade,
	};

	const getHealthSpecialtiesSectionTitle = () => {
		if (
			isSemanticSearchEnabled &&
			provider.tipo.toUpperCase() === HealthGuideServicesTypeEnum.AUXILIARY_SERVICES
		) {
			return guideDetails.procedures;
		}
		return guideDetails.specialties;
	};

	const emptyData =
		!loading &&
		providerDetail?.especialidades?.length === 0 &&
		!healthServicesData.careTypes &&
		!healthServicesData.servicesType &&
		!healthServicesData.appointment &&
		!healthServicesData.hospitalization &&
		!healthServicesData.maternity;

	return (
		<StyledContainer>
			<Typography variant="subtitle2" color="text.secondary">
				{guideDetails.services}
			</Typography>
			{emptyData ? (
				<Typography variant="body2" color="text.secondary">
					{guideDetails.emptyData}
				</Typography>
			) : (
				<Stack gap={2}>
					<StyledStack>
						{healthServicesData.careTypes ? (
							<DataInfo
								primaryTextFontSize="1rem"
								secondaryTextFontSize="0.875rem"
								secondaryTextColor="text.secondary"
								primaryTextColor="text.primary"
								removeItemTextMargin
								iconName="clipboard-plus-outline"
								primaryText={guideDetails.careTypes}
								secondaryText={
									healthServicesData.careTypes
										? healthServicesData.careTypes
										: guideDetails.emptyData
								}
								removePaddingBottom
							/>
						) : null}

						{healthServicesData.servicesType ? (
							<DataInfo
								primaryTextFontSize="1rem"
								secondaryTextFontSize="0.875rem"
								secondaryTextColor="text.secondary"
								primaryTextColor="text.primary"
								removeItemTextMargin
								iconName="clipboard-plus-outline"
								primaryText={guideDetails.servicesTypes}
								secondaryText={
									healthServicesData.servicesType
										? healthServicesData.servicesType
										: guideDetails.emptyData
								}
								removePaddingBottom
							/>
						) : null}

						{isHideSpecialtiesProceduresFlagEnabled ? null : (
							<HealthSpecialtiesSection
								title={getHealthSpecialtiesSectionTitle()}
								data={providerDetail}
								hasError={isError}
								loading={loading}
								iconName={serviceInfo.icon}
							/>
						)}
					</StyledStack>
					{healthServicesData.appointment ? (
						<DataInfo
							primaryTextFontSize="1rem"
							primaryTextColor="text.primary"
							removeItemTextMargin
							iconName="calendar-clock"
							primaryText={guideDetails.appointment}
							secondaryText={healthServicesData.appointment}
							removePaddingBottom
						/>
					) : null}
					{healthServicesData.hospitalization ? (
						<DataInfo
							primaryTextFontSize="1rem"
							primaryTextColor="text.primary"
							removeItemTextMargin
							iconName="hotel-bed-line"
							primaryText={guideDetails.hospitalization}
							secondaryText={healthServicesData.hospitalization}
							removePaddingBottom
						/>
					) : null}
					{healthServicesData.maternity ? (
						<DataInfo
							primaryTextFontSize="1rem"
							primaryTextColor="text.primary"
							removeItemTextMargin
							iconName="child-care"
							primaryText={guideDetails.maternity}
							secondaryText={healthServicesData.maternity}
							removePaddingBottom
						/>
					) : null}
				</Stack>
			)}
		</StyledContainer>
	);
}
